import { CacheControl } from '@tusbar/cache-control'
import Router from 'next/router'

import { ApolloClientProfile } from '@syconium/weeping-figs'

import { QuickBuyModal } from '../../../../brunswick/components/QuickBuyModal'
import { CollectionFilterQueryParamsContextProvider } from '../../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { QuickBuyContainer } from '../../../../brunswick/containers/QuickBuyContainer'
import { FETCH_SEARCH_REDIRECTS } from '../../../../lib/graphql/queries'
import { cachePolicies } from '../../../../lib/response-cache'
import { singleQueryParamValue } from '../../../../lib/utils'
import { RedirectSet } from '../../../../types/graphql'
import { MagnoliaPage } from '../../MagnoliaApp'

import { primeClientForUseSearchPageQuery } from './hooks/useSearchPageQuery'
import { SearchPageView } from './SearchPageView'

export const SEARCH_REGEX =
  /^[a-zA-Z0-9 ™\!\@\#\$\%\^\&\*\(\)\_\+\-\=\`\~\\\/\?\<\>\,\.\;\:\'\"\|]*$/

export const SearchPage: MagnoliaPage<{
  error: Error | undefined
  query: string
  resultsChangedTimestamp: Date
  statusCode: number | undefined
}> = ({ error, query, resultsChangedTimestamp, statusCode }) => {
  return (
    <QuickBuyContainer.Provider>
      <CollectionFilterQueryParamsContextProvider>
        <SearchPageView
          query={query}
          resultsChangedTimestamp={resultsChangedTimestamp}
          statusCode={statusCode}
          error={error}
        />
      </CollectionFilterQueryParamsContextProvider>
      <QuickBuyModal />
    </QuickBuyContainer.Provider>
  )
}

SearchPage.getInitialProps = async function (context) {
  const client = context.apolloClientFactory.for(ApolloClientProfile.FigsPublicSupergraph)
  const query = singleQueryParamValue(context.query.q, '')
  let error: Error | undefined = undefined

  const isValidSearchQuery = SEARCH_REGEX.exec(query) !== null

  if (!isValidSearchQuery && context.res) {
    context.res.statusCode = 410
  }

  if (query && query.trim()) {
    try {
      // @TODO: remove `any` generics for full type-safety
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data } = await client.query<any, any>({
        query: FETCH_SEARCH_REDIRECTS,
        variables: {
          handle: 'default',
        },
      })

      if (data?.siteFixtures?.searchRedirects) {
        const searchRedirects: RedirectSet = data.siteFixtures.searchRedirects
        const statusCode = searchRedirects?.code ?? 307
        const redirects = JSON.parse(searchRedirects.redirects)
        const searchTerm = query.toLowerCase()

        if (redirects[searchTerm]) {
          if (context.res) {
            context.res.writeHead(statusCode, { Location: redirects[searchTerm] })
            context.res.end()
          } else {
            Router.replace(redirects[searchTerm])
          }
        }
      }
    } catch (err) {
      error = err as Error
    }

    try {
      await primeClientForUseSearchPageQuery({ client, query })
    } catch (err) {
      error = err as Error
    }
  }

  return {
    error,
    query,
    responseCacheControl: cachePolicy(error),
    /**
     * Since searches cause a route navigation from this page to itself and
     * the query results may be identical (and thus React doesn’t know anything
     * has changed), we use a timestamp to trigger the removal of the "loading" UI.
     */
    resultsChangedTimestamp: new Date(Date.now()),
    statusCode: context.res?.statusCode,
  }
}

function cachePolicy(error: Error | undefined): CacheControl {
  return error ? cachePolicies.noCache : cachePolicies.publicHtmlPolicy
}
